import { proxy } from "valtio";
import { modelPatterns } from "./patterns";
import Shoe from "../Components/Shoe";
import Rocket from "../Components/Rocket";
import Axe from "../Components/Axe";
import Insect from "../Components/Insect";
import Teapot from "../Components/Teapot";
import Sneaker from "../Components/Sneaker";
import PoloShirt from "../Components/PoloShirt";
import HighNeckTshirt from "../Components/HighNeckTshirt";
import Hoodie from "../Components/Hoodie";
import BoxingX1 from "../Components/BoxingX1";

// ============================================================
// MODEL CONFIGURATION
// Add/remove/modify models here. Everything else adapts automatically.
// Each model needs: component, default colors, camera angles per part.
// ============================================================

const modelConfig = {
  Shoe: {
    component: Shoe,
    placements: {
      front: { label: "Front", dir: "front", rayHeight: 0.35 },
      back:  { label: "Back",  dir: "back",  rayHeight: 0.35 },
      left:  { label: "Left",  dir: "left",  rayHeight: 0.5 },
      right: { label: "Right", dir: "right", rayHeight: 0.5 },
    },
    colors: {
      laces: "#d3d3d3",
      mesh: "#d3d3d3",
      caps: "#d3d3d3",
      inner: "#d3d3d3",
      sole: "#d3d3d3",
      stripes: "#d3d3d3",
      band: "#d3d3d3",
      patch: "#d3d3d3",
    },
    // Camera angles: [azimuthal, polar] in radians
    cameraAngles: {
      laces: [0, 1.2],
      mesh: [0.5, 1.4],
      caps: [2.5, 1.3],
      inner: [1.8, 0.8],
      sole: [0.3, 2.2],
      stripes: [-0.8, 1.3],
      band: [-1.5, 1.4],
      patch: [3.0, 1.2],
    },
  },
  Rocket: {
    component: Rocket,
    placements: {
      hullFront: { label: "Hull Front", dir: "front", rayHeight: 0.5 },
      hullBack:  { label: "Hull Back",  dir: "back",  rayHeight: 0.5 },
    },
    colors: {
      hull: "#d3d3d3",
      base: "#d3d3d3",
      tip: "#d3d3d3",
      wings: "#a8a8a8",
      window: "#a8a8a8",
    },
    cameraAngles: {
      hull: [0, 1.4],
      base: [0, 2.0],
      tip: [0, 0.6],
      wings: [0.8, 1.8],
      window: [-0.3, 1.0],
    },
  },
  Axe: {
    component: Axe,
    placements: {
      blade:  { label: "Blade",  dir: "front", rayHeight: 0.7 },
      handle: { label: "Handle", dir: "front", rayHeight: 0.2 },
    },
    colors: {
      body: "#a8a8a8",
      design: "#d3d3d3",
      support: "#d3d3d3",
      inner: "#d3d3d3",
    },
    cameraAngles: {
      body: [0, 1.4],
      design: [0.6, 1.2],
      support: [-0.5, 1.5],
      inner: [1.2, 1.0],
    },
  },
  Insect: {
    component: Insect,
    placements: {
      shell: { label: "Shell", dir: "top",   rayHeight: 0.9 },
      body:  { label: "Body",  dir: "front", rayHeight: 0.35 },
    },
    colors: {
      body: "#d3d3d3",
      shell: "#a8a8a8",
    },
    cameraAngles: {
      body: [0, 1.2],
      shell: [0, 0.7],
    },
  },
  Teapot: {
    component: Teapot,
    placements: {
      body: { label: "Body", dir: "front", rayHeight: 0.35 },
      lid:  { label: "Lid",  dir: "top",   rayHeight: 1.0 },
    },
    colors: {
      lid: "#d3d3d3",
      base: "#a8a8a8",
    },
    cameraAngles: {
      lid: [0, 0.6],
      base: [0.5, 1.8],
    },
  },
  Sneaker: {
    component: Sneaker,
    cameraPosition: [0, 0, 1.5],
    minDistance: 0.5,
    placements: {
      front: { label: "Front", dir: "front", rayHeight: 0.35 },
      back:  { label: "Back",  dir: "back",  rayHeight: 0.35 },
      left:  { label: "Left",  dir: "left",  rayHeight: 0.5 },
      right: { label: "Right", dir: "right", rayHeight: 0.5 },
    },
    colors: {
      laces: "#ffffff",
      front: "#ffffff",
      middle: "#ffffff",
      top: "#ffffff",
      flaps: "#ffffff",
      sole: "#e0e0e0",
      strap: "#ffffff",
      inside: "#d3d3d3",
      stitches: "#cccccc",
    },
    // Toggleable parts: { key: { label, default } }
    options: {
      strap: { label: "Back Strap", default: true },
    },
    cameraAngles: {
      laces: [0, 1.2],
      front: [0.3, 1.3],
      middle: [-0.8, 1.4],
      top: [0, 0.8],
      flaps: [1.5, 1.3],
      sole: [0.3, 2.2],
      strap: [0, 1.0],
      inside: [1.8, 0.8],
      stitches: [0.5, 1.5],
    },
  },
  PoloShirt: {
    component: PoloShirt,
    cameraPosition: [0, 0.2, 2],
    placements: {
      chest:       { label: "Chest",        dir: "front", rayHeight: 0.35 },
      back:        { label: "Back",         dir: "back",  rayHeight: 0.35 },
      leftSleeve:  { label: "Left Sleeve",  dir: "left",  rayHeight: 0.5 },
      rightSleeve: { label: "Right Sleeve", dir: "right", rayHeight: 0.5 },
    },
    colors: {
      body: "#ffffff",
      buttons: "#cccccc",
      sleeves: "#ffffff",
    },
    cameraAngles: {
      body: [0, 1.2],
      buttons: [0, 1.0],
      sleeves: [0.8, 1.3],
    },
    customOptions: {
      enabled: true,
      currency: "USD",
      basePrice: 25.00,
      branding: {
        companyName: "Your Brand",
        tagline: "Premium Custom Products",
        // logoUrl: "/logo.png",  // optional — base64 or URL to company logo
      },
      features: {
        qrCode: true,        // QR code in PDF linking to shareable config
        annotations: true,   // Draw/arrow/text annotations on captured views
        shareLink: true,     // Copy shareable link button
      },
      groups: [
        {
          key: "logoType", label: "Logo Type", type: "select",
          choices: [
            { key: "none", label: "No Logo", price: 0 },
            { key: "print", label: "Screen Print", price: 5.00 },
            { key: "embroidery", label: "Embroidery", price: 12.00 },
          ],
          default: "none",
        },
        {
          key: "fabric", label: "Fabric", type: "select",
          choices: [
            { key: "cotton", label: "100% Cotton", price: 0 },
            { key: "polyester", label: "100% Polyester", price: 2.00 },
            { key: "blend", label: "Cotton-Poly Blend", price: 1.50 },
          ],
          default: "cotton",
        },
        { key: "rushOrder", label: "Rush Production", type: "toggle", price: 15.00, default: false },
      ],
    },
  },
  HighNeckTshirt: {
    component: HighNeckTshirt,
    cameraPosition: [0, 0.2, 2],
    placements: {
      chest:       { label: "Chest",        dir: "front", rayHeight: 0.35 },
      back:        { label: "Back",         dir: "back",  rayHeight: 0.35 },
      leftSleeve:  { label: "Left Sleeve",  dir: "left",  rayHeight: 0.72 },
      rightSleeve: { label: "Right Sleeve", dir: "right", rayHeight: 0.72 },
    },
    colors: {
      body: "#c8c8c8",
    },
    cameraAngles: {
      body: [0, 1.2],
    },
    // Design presets — transparent overlay textures per part
    designs: [
      {
        label: "Basic",
        thumb: "/highneck-tshirt/thumbs/basic-tshirt.png",
        textures: {},
      },
      {
        label: "Double Lines",
        thumb: "/highneck-tshirt/thumbs/double-lines.png",
        textures: {
          front: ["/highneck-tshirt/textures/double-lines/front_1.png"],
          back:  ["/highneck-tshirt/textures/double-lines/back_1.png"],
        },
      },
      {
        label: "Tribal Lines",
        thumb: "/highneck-tshirt/thumbs/trible-lines.png",
        textures: {
          front: ["/highneck-tshirt/textures/trible-lines/front_1.png"],
          back:  ["/highneck-tshirt/textures/trible-lines/back_1.png"],
        },
      },
      {
        label: "Lines",
        thumb: "/highneck-tshirt/thumbs/basic-tshirt.png",
        textures: {
          front:       ["/highneck-tshirt/textures/lines/front_1.png", "/highneck-tshirt/textures/lines/front_2.png"],
          back:        ["/highneck-tshirt/textures/lines/back_1.png", "/highneck-tshirt/textures/lines/back_2.png"],
          rightSleeve: ["/highneck-tshirt/textures/lines/right_1.png"],
          leftSleeve:  ["/highneck-tshirt/textures/lines/left_1.png"],
        },
      },
    ],
  },
  Hoodie: {
    component: Hoodie,
    cameraPosition: [0, 0, 2],
    placements: {
      chest:  { label: "Chest",  dir: "front", rayHeight: 0.45 },
      back:   { label: "Back",   dir: "back",  rayHeight: 0.45 },
      hood:   { label: "Hood",   dir: "front", rayHeight: 0.85 },
      pocket: { label: "Pocket", dir: "front", rayHeight: 0.15 },
    },
    colors: {
      body:       "#d0d0d0",
      hood:       "#d0d0d0",
      drawstring: "#e8e8e8",
      bottom:     "#e8e8e8",
      cuff:       "#d0d0d0",
      pocket:     "#e8e8e8",
    },
    cameraAngles: {
      body:       [0, 1.2],
      hood:       [0, 0.8],
      drawstring: [0, 0.9],
      bottom:     [0, 1.8],
      cuff:       [0.8, 1.5],
      pocket:     [0, 1.4],
    },
    customOptions: {
      enabled: true,
      currency: "USD",
      basePrice: 35.00,
      branding: {
        companyName: "Your Brand",
        tagline: "Premium Custom Products",
      },
      features: {
        qrCode: true,
        undoRedo: true,
        annotations: true,
        shareLink: true,
      },
      groups: [
        {
          key: "logoType", label: "Logo Type", type: "select",
          choices: [
            { key: "none", label: "No Logo", price: 0 },
            { key: "print", label: "Screen Print", price: 5.00 },
            { key: "embroidery", label: "Embroidery", price: 14.00 },
          ],
          default: "none",
        },
        {
          key: "fabric", label: "Fabric", type: "select",
          choices: [
            { key: "fleece", label: "Cotton Fleece", price: 0 },
            { key: "polyester", label: "Polyester Fleece", price: 3.00 },
            { key: "organic", label: "Organic Cotton", price: 6.00 },
          ],
          default: "fleece",
        },
        {
          key: "lining", label: "Inner Lining", type: "select",
          choices: [
            { key: "standard", label: "Standard", price: 0 },
            { key: "sherpa", label: "Sherpa Lining", price: 8.00 },
          ],
          default: "standard",
        },
        { key: "rushOrder", label: "Rush Production", type: "toggle", price: 15.00, default: false },
      ],
    },
  },
  BoxingX1: {
    component: BoxingX1,
    cameraPosition: [0, 0, 2],
    finishOptions: true, // enables Plain/Matte/Metallic/Fluorescent finish tabs in ColorPicker
    placements: {
      palmback: { label: "Back Palm",   dir: "front", rayHeight: 0.8 },
      strap:    { label: "Velcro Strap",dir: "front", rayHeight: 0.1 },
      stitches: { label: "Stitching",   dir: "front", rayHeight: 0.2 },
    },
    colors: {
      black: "#1a1a1a",
      wristback: "#1a1a1a",
      piping: "#ffffff",
      strap: "#1a1a1a",
      tstrip: "#1a1a1a",
      palm: "#1a1a1a",
      connect: "#cccccc",
      thumbin: "#1a1a1a",
      velcro: "#333333",
      thumbout: "#1a1a1a",
      palmback: "#1a1a1a",
      stitches: "#ffffff",
      p1: "#1a1a1a",
      p2: "#1a1a1a",
      p4: "#1a1a1a",
    },
    cameraAngles: {
      black: [0, 1.2],
      wristback: [Math.PI, 1.3],
      piping: [0.3, 1.2],
      strap: [0, 1.4],
      tstrip: [-1.0, 1.2],
      palm: [-1.5, 1.3],
      connect: [-0.9, 1.0],
      thumbin: [-1.0, 1.2],
      velcro: [Math.PI, 1.0],
      thumbout: [-1.2, 1.1],
      palmback: [0, 1.0],
      stitches: [0, 1.2],
      p1: [0.5, 0.8],
      p2: [0.5, 1.6],
      p4: [0.5, 0.8],
    },
    customOptions: {
      enabled: true,
      currency: "USD",
      basePrice: 30.00,
      branding: {
        companyName: "Your Brand",
        tagline: "Premium Custom Products",
      },
      features: {
        qrCode: true,
        annotations: true,
        shareLink: true,
      },
      groups: [
        {
          key: "logoType", label: "Logo Type", type: "select",
          choices: [
            { key: "none", label: "No Logo", price: 0 },
            { key: "print", label: "Screen Print", price: 5.00 },
            { key: "embroidery", label: "Embroidery", price: 12.00 },
          ],
          default: "none",
        },
        {
          key: "material", label: "Material", type: "select",
          choices: [
            { key: "synthetic", label: "Synthetic Leather", price: 0 },
            { key: "genuine", label: "Genuine Leather", price: 15.00 },
          ],
          default: "synthetic",
        },
        { key: "rushOrder", label: "Rush Production", type: "toggle", price: 15.00, default: false },
      ],
    },
  },
};

// Auto-generate proxy states from config
const modelStates = {};
Object.keys(modelConfig).forEach((name) => {
  const opts = modelConfig[name].options || {};
  const optionDefaults = {};
  Object.keys(opts).forEach((key) => {
    optionDefaults[key] = opts[key].default;
  });
  // Generate default texture state — null means use original GLB texture
  const partPatterns = modelPatterns[name] || {};
  const textureDefaults = {};
  Object.keys(partPatterns).forEach((part) => { textureDefaults[part] = null; });
  // Generate default finish state — "plain" for models with finishOptions enabled
  const finishDefaults = {};
  if (modelConfig[name].finishOptions) {
    Object.keys(modelConfig[name].colors).forEach((part) => { finishDefaults[part] = "plain"; });
  }

  modelStates[name] = proxy({
    current: null,
    colors: { ...modelConfig[name].colors },
    options: optionDefaults,
    textures: textureDefaults,
    finishes: finishDefaults,
    design: modelConfig[name].designs ? 0 : null,
    designColor: modelConfig[name].designs ? "#000000" : null,
  });
});

export { modelConfig, modelStates };
export const modelNames = Object.keys(modelConfig);
export const defaultModel = modelNames[0];
export function getDefaultPlacement(modelName) {
  const cfg = modelConfig[modelName];
  if (cfg?.placements) return Object.keys(cfg.placements)[0];
  return "front";
}
